import React from 'react';
import { useForm } from 'react-hook-form';
import { useEnquiryStore } from '../../store/useEnquiryStore';
import { Outcome } from '../../types';

export const OutcomeForm: React.FC = () => {
  const { currentEnquiry, updateOutcome } = useEnquiryStore();
  const { register, handleSubmit, formState: { errors } } = useForm<Outcome>({
    defaultValues: currentEnquiry.outcome
  });

  const onSubmit = (data: Outcome) => {
    updateOutcome(data);
  };

  const handleInputChange = (field: keyof Outcome, value: string | boolean) => {
    updateOutcome({ [field]: value });
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Enquiry Outcome</h2>
        <p className="text-sm text-gray-600">Record the final outcome of the enquiry and any follow-up actions.</p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-3">
            Outcome Status <span className="text-red-500">*</span>
          </label>
          <div className="space-y-2">
            <div className="flex items-center">
              <input
                type="radio"
                id="successful"
                value="Successful"
                {...register('status', { required: 'Please select an outcome status' })}
                onChange={(e) => handleInputChange('status', e.target.value)}
                className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300"
              />
              <label htmlFor="successful" className="ml-2 block text-sm text-gray-900">
                Successful
              </label>
            </div>
            <div className="flex items-center">
              <input
                type="radio"
                id="unsuccessful"
                value="Unsuccessful"
                {...register('status', { required: 'Please select an outcome status' })}
                onChange={(e) => handleInputChange('status', e.target.value)}
                className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300"
              />
              <label htmlFor="unsuccessful" className="ml-2 block text-sm text-gray-900">
                Unsuccessful
              </label>
            </div>
          </div>
          {errors.status && <p className="mt-1 text-sm text-red-600">{errors.status.message}</p>}
        </div>

        <div>
          <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-1">
            Outcome Notes
          </label>
          <textarea
            id="notes"
            {...register('notes')}
            onChange={(e) => handleInputChange('notes', e.target.value)}
            rows={4}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="Describe the outcome, reasons for success/failure, customer feedback, etc."
          />
        </div>

        <div>
          <div className="flex items-center">
            <input
              type="checkbox"
              id="flagForAccounts"
              {...register('flagForAccounts')}
              onChange={(e) => handleInputChange('flagForAccounts', e.target.checked)}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label htmlFor="flagForAccounts" className="ml-2 block text-sm text-gray-900">
              Flag for Accounts Department
            </label>
          </div>
          <p className="mt-1 text-sm text-gray-600">
            Check if this enquiry requires attention from the accounts department (e.g., payment issues, special billing arrangements).
          </p>
        </div>

        <div className={`p-4 rounded-md ${
          currentEnquiry.outcome.status === 'Successful' ? 'bg-green-50' : 'bg-red-50'
        }`}>
          <div className="flex">
            <div className="flex-shrink-0">
              {currentEnquiry.outcome.status === 'Successful' ? (
                <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              ) : (
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              )}
            </div>
            <div className="ml-3">
              <h3 className={`text-sm font-medium ${
                currentEnquiry.outcome.status === 'Successful' ? 'text-green-800' : 'text-red-800'
              }`}>
                {currentEnquiry.outcome.status === 'Successful' ? 'Successful Enquiry' : 'Unsuccessful Enquiry'}
              </h3>
              <div className={`mt-2 text-sm ${
                currentEnquiry.outcome.status === 'Successful' ? 'text-green-700' : 'text-red-700'
              }`}>
                {currentEnquiry.outcome.status === 'Successful' ? (
                  <ul className="list-disc list-inside space-y-1">
                    <li>Proceed with order processing</li>
                    <li>Schedule installation if applicable</li>
                    <li>Send confirmation to customer</li>
                    <li>Update customer records</li>
                  </ul>
                ) : (
                  <ul className="list-disc list-inside space-y-1">
                    <li>Record reason for unsuccessful outcome</li>
                    <li>Consider follow-up opportunities</li>
                    <li>Review pricing or product offering</li>
                    <li>Update customer for future reference</li>
                  </ul>
                )}
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};